// NPM
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useParams } from "react-router-dom";
import { isNull, omitBy, isNil, isEmpty } from "lodash";

// UTILITY
import errorHandler from "Utils/error-handler";

// VALIDATOR
import { LocationCreateSchema } from "./location-form-validation";

// IMPORT DATA
import statePostcodeList from "../../../data/state_malaysia.json";

// STYLE
import "./location-form.scss";

// HOOKS
import useSession from "Hooks/session.hook";

// ANT COMPONENTS
import { Col, Row, Checkbox, Divider } from "antd";
import {
  AlertBox,
  Button,
  DateRange,
  SelectInput,
  TextInput,
} from "@life-line-lab/components-react";
import useQuery from "Hooks/query.hook";
import { OrderSettingsEnum } from "Data/settings";

interface LocationFormComponentProps {
  info?: any;
  onFetch: any;
  settingOptions?: any;
  onSubmit?: any;
  onUpdate?: any;
}

const LocationFormComponent: React.FC<LocationFormComponentProps> = ({
  info,
  onFetch,
  settingOptions,
  onSubmit,
  onUpdate,
}) => {
  const { org } = useSession();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const type = useQuery().get("type");
  const isView = type == "view";

  const initialInput = {
    areaName: "",
    states: [],
    postcodes: {},
    deliveryTypes: [],
    isFixedAmount: false,
    fixedAmount: "",
    isPercentage: false,
    percentage: "",
    capAmount: "",
    validFrom: {
      date: null,
      dateString: "",
    },
    validTo: {
      date: moment("31/12/9999", "DD/MM/YYYY"),
      dateString: moment("31/12/9999", "DD/MM/YYYY").toISOString(),
    },
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [stateOptions, setStateOptions] = useState<any>([]);
  const [postcodeOptions, setPostcodeOptions] = useState({});

  useEffect(() => {
    setStateOptions(
      statePostcodeList.map((state) => {
        return {
          label: state.Name,
          value: state.Name,
        };
      }),
    );
    onFetch({
      orgID: org._id,
      type: OrderSettingsEnum.DELIVERY_TYPE,
      currentVersionOnly: true,
    });
    document.body.classList.add("stack-screen--open");
    return () => document.body.classList.remove("stack-screen--open");
  }, []);

  useEffect(() => {
    const selectAllOpt = [
      {
        label: "Select All",
        value: "Select All",
      },
    ];
    input?.states?.forEach((inputState) => {
      const postOptions = statePostcodeList
        .find((state) => state.Name === inputState)
        ?.Postcode.sort()
        .map((postcodeData) => {
          return {
            label: postcodeData,
            value: postcodeData,
          };
        });
      setPostcodeOptions((postcodeOptions) => {
        return {
          ...postcodeOptions,
          [inputState]: selectAllOpt.concat(postOptions as any),
        };
      });
    });
  }, [input.states]);

  useEffect(() => {
    if (!isNil(info)) {
      setInput({
        ...input,
        ...info,
        deliveryTypes: info.deliveryTypes,
        states: info.states?.map((state) => state.state),
        postcodes: info.states?.reduce((previous, current) => {
          return {
            ...previous,
            [current.state]: current.postcodes,
          };
        }, {}),
        validFrom: { date: info.validFrom, dateString: moment(info.validFrom).toISOString() },
        validTo: { date: info.validTo, dateString: moment(info.validTo).toISOString() },
      });
    }
  }, [info]);

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value: string | string[], name: string) => {
    setError(initialError);
    setInput({ ...input, [name]: value });
  };

  const handleCheckboxChange = (checked: boolean, name: string) => {
    if (isView) {
      return;
    }
    setError(initialError);
    setInput({ ...input, [name]: checked });
  };

  const handleDateChange = (startDate: any, endDate: any) => {
    if (startDate.date) {
      setError(initialError);
      setInput({ ...input, validFrom: startDate });
    } else if (endDate.date) {
      setError(initialError);
      setInput({ ...input, validTo: endDate });
    }
  };

  const handlePostChange = (e: string[], state: string) => {
    setError(initialError);
    e.find((element) => element === "Select All")
      ? setInput({
          ...input,
          postcodes: {
            ...input.postcodes,
            [state]: postcodeOptions[state]
              .map((postcode) => postcode.value)
              .filter((postcode) => postcode != "Select All"),
          },
        })
      : setInput({ ...input, postcodes: { ...input.postcodes, [state]: e } });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
        settingID: info?._id,
        states: input.states.map((state) => {
          return omitBy(
            {
              state: state,
              postcodes: input["postcodes"][state],
            },
            isEmpty,
          );
        }),
        surcharge: omitBy(
          {
            // selected but empty attr is unvalidated if pair is valid
            fixedAmount: input.isFixedAmount ? input.fixedAmount : "",
            percentage: input.isPercentage ? input.percentage : "",
            capAmount: input.capAmount,
          },
          isEmpty,
        ),
        validFrom: moment(input.validFrom?.date).toISOString(),
        validTo: moment(input.validTo?.date).toISOString(),
      };
      const value = LocationCreateSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });
      info ? onUpdate(value) : onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({ ...error, [path]: t(`${value}`, { field: t(`label.${path}`) }) });
    }
  };

  return (
    <div className="location-form">
      <form>
        <div className="main-header">
          <span className="center header-title">{t("label.locations").toUpperCase()}</span>
        </div>
        <Row gutter={[20, 0]} className="mt-40">
          <Col xs={24} sm={24} md={8} className="mb-30">
            <TextInput
              isRequired={true}
              isReadonly={isView}
              label={t("label.areaName")}
              placeholder={t("txt.enterLocationsHere")}
              value={input.areaName}
              name="areaName"
              onChange={handleFieldChange}
            />
            <AlertBox text={error.areaName} />
          </Col>
          <Col xs={24} sm={24} md={8} className="mb-30">
            <SelectInput
              showSearch
              isReadonly={isView}
              isRequired={true}
              label={t("label.deliveryType")}
              placeholder={t("txt.selectHere")}
              value={input.deliveryTypes}
              options={
                settingOptions[OrderSettingsEnum.DELIVERY_TYPE]?.map((delivery) => {
                  return {
                    label: delivery?.name,
                    value: delivery?.code,
                  };
                }) || []
              }
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="deliveryTypes"
              onChange={(value) => handleSelectChange(value, "deliveryTypes")}
              mode="multiple"
              showArrow={true}
              allowClear
            />
            <AlertBox text={error.deliveryTypes} />
          </Col>
          <Col xs={24} sm={24} md={8} className="mb-30">
            <SelectInput
              showSearch
              isReadonly={isView}
              isRequired={true}
              label={t("label.states")}
              placeholder={t("txt.selectStatesHere")}
              value={input.states}
              options={stateOptions}
              filterOption={(inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
              }
              name="states"
              onChange={(value) => {
                setPostcodeOptions({});
                handleSelectChange(value, "states");
              }}
              mode="multiple"
              showArrow={true}
              allowClear
            />
            <AlertBox text={error.states} />
          </Col>
        </Row>
        <Divider className="mb-0 mt-3" />
        <Row gutter={[20, 0]} className="mt-25">
          {!isEmpty(postcodeOptions) &&
            input?.states?.map((state, index) => {
              return (
                <Col xs={24} sm={24} md={8} className="mb-30" key={index}>
                  <SelectInput
                    showSearch
                    isRequired={true}
                    isReadonly={isView}
                    label={t("label.postcodes") + ` - ${state}`}
                    placeholder={t("txt.selectHere")}
                    value={input["postcodes"] ? input["postcodes"][state] : null}
                    options={postcodeOptions[state]}
                    filterOption={(inputValue, option) =>
                      option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    name="postcodes"
                    onChange={(e) => handlePostChange(e as string[], state)}
                    mode="multiple"
                    showArrow={true}
                    allowClear
                  />
                  <AlertBox
                    text={
                      error[`states[${index}].postcodes`]
                        ? t("error.postcodes", { state: state })
                        : null
                    }
                  />
                </Col>
              );
            })}
        </Row>
        <Divider className="mb-0 mt-3" />
        <Row gutter={[20, 0]} className="mt-25">
          <Col xs={24} sm={24} md={16} className="mb-20">
            <label className="inp-label inp-label--required">{t("label.surcharge")}</label>
            <Row gutter={[20, 0]} align="middle">
              <Col xs={24} sm={24} md={8} className="mb-9 pr-0">
                <Checkbox
                  checked={input.isFixedAmount}
                  onChange={(e) => handleCheckboxChange(e.target.checked, "isFixedAmount")}
                >
                  {t("label.fixedAmount")}
                </Checkbox>
              </Col>
              <Col xs={24} sm={24} md={16} className="mb-9">
                <TextInput
                  isRequired={true}
                  isReadonly={!input.isFixedAmount || isView}
                  prefix="RM"
                  value={input.fixedAmount}
                  name="fixedAmount"
                  onChange={handleFieldChange}
                />
              </Col>
            </Row>
            <Row gutter={[18, 0]} align="middle">
              <Col xs={24} sm={24} md={8} className="mb-9 pr-0">
                <Checkbox
                  checked={input.isPercentage}
                  onChange={(e) => handleCheckboxChange(e.target.checked, "isPercentage")}
                >
                  {t("label.percentage")}
                </Checkbox>
              </Col>
              <Col xs={24} sm={24} md={16} className="mb-0">
                <TextInput
                  isRequired={true}
                  isReadonly={!input.isPercentage || isView}
                  value={input.percentage}
                  name="percentage"
                  suffix="%"
                  onChange={handleFieldChange}
                />
              </Col>
            </Row>
            {input.isPercentage ? (
              <Row gutter={[20, 0]} align="middle">
                <Col xs={24} sm={24} md={8} className="mt-10 mb-0 pr-0">
                  <label className="ml-24">{t(`label.capAmount`)}</label>
                </Col>
                <Col xs={24} sm={24} md={16} className="mt-10 mb-0">
                  <TextInput
                    isRequired={true}
                    isReadonly={isView}
                    prefix={t("txt.rm")}
                    value={input.capAmount}
                    name="capAmount"
                    onChange={handleFieldChange}
                  />
                </Col>
              </Row>
            ) : (
              <></>
            )}
            <Row>
              <Col span={24} className="mb-0 alert-box">
                <AlertBox
                  text={
                    error["surcharge.fixedAmount"] || error["surcharge.percentage"]
                      ? t("error.surcharge")
                      : error["surcharge.capAmount"]
                      ? t(`error.pattern`, { field: t(`label.capAmount`) })
                      : null
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={[20, 0]} className="mt-10" align="bottom">
          {!isView ? (
            <Col xs={24} sm={24} md={16} className="mb-0">
              <DateRange
                isRequired={true}
                startLabel={t("label.validFrom")}
                endLabel={t("label.validTo")}
                value={{
                  startDate: input.validFrom,
                  endDate: input.validTo,
                }}
                onChange={handleDateChange}
              />
              <AlertBox text={error.validFrom || error.validTo} />
            </Col>
          ) : (
            <Col xs={24} sm={24} md={16} className="mb-0">
              <label className="inp-label inp-label--required">Validity</label>
              <div>
                {moment(input.validFrom?.date).format("DD-MM-YYYY")} to{" "}
                {moment(input.validTo?.date).format("DD-MM-YYYY")}
              </div>
            </Col>
          )}
        </Row>
        {!isView ? (
          <Row className="mt-50" justify="center">
            <Col className="mb-50">
              <Button
                size="large"
                type="primary"
                onClick={handleSubmit}
                label={id ? t("action.update") : t("action.create")}
              />
            </Col>
          </Row>
        ) : null}
      </form>
    </div>
  );
};

export default LocationFormComponent;
